import React, { useMemo } from 'react';
import { Button, Container, Grid, Icon, Label, Loader, Message, Segment } from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import {
  pageBodyStyle,
  pageFrameStyle,
  pageShellStyle,
  sectionTitleStyle,
  softInfoCardStyle,
  surfaceCardStyle,
} from '../misc/pageStyles';
import { useStockInconsistencySummary } from '../context/StockInconsistencySummaryContext';
import { useAuth } from '../context/AuthContext';
import { useFirstRunOnboarding } from '../../hooks/useFirstRunOnboarding';
import AccountImportCard from '../account/AccountImportCard';

const quickSections = [
  {
    key: 'stock',
    title: 'Stock',
    description: 'Ajustá stock, filtros y publicaciones existentes.',
    icon: 'cubes',
    to: '/stock',
    cta: 'Ir a stock',
  },
  {
    key: 'diferencias',
    title: 'Alertas de stock',
    description: 'Resolvé publicaciones sin registrar y desvíos de stock.',
    icon: 'warning sign',
    to: '/stock-inconsistencies',
    cta: 'Resolver alertas',
  },
  {
    key: 'movimientos',
    title: 'Movimientos',
    description: 'Auditá ventas, ajustes y fan-out de stock.',
    icon: 'exchange',
    to: '/movimientos',
    cta: 'Ver movimientos',
  },
  {
    key: 'ordenes',
    title: 'Órdenes',
    description: 'Seguimiento de órdenes y estados de marketplace.',
    icon: 'shopping cart',
    to: '/ordenes',
    cta: 'Ver órdenes',
  },
  {
    key: 'canales',
    title: 'Canales',
    description: 'Conectá cuentas e importá publicaciones existentes o nuevas.',
    icon: 'plug',
    to: '/canales',
    cta: 'Gestionar canales',
  },
];

const getAccountName = (account) =>
  account?.nickname || account?.name || account?.email || account?.id || 'Cuenta conectada';

function OnboardingLoadingState() {
  return (
    <Container fluid style={pageShellStyle}>
      <Segment style={pageFrameStyle}>
        <div style={{ ...pageBodyStyle, minHeight: 320, display: 'grid', placeItems: 'center' }}>
          <div role="status" aria-live="polite" style={{ textAlign: 'center' }}>
            <Loader active inline="centered" size="large" />
            <div style={{ marginTop: 18, color: '#6B7280' }}>Preparando tu inicio…</div>
          </div>
        </div>
      </Segment>
    </Container>
  );
}

function FirstAccountOnboarding() {
  return (
    <Container fluid style={pageShellStyle}>
      <Segment style={pageFrameStyle}>
        <div
          style={{
            padding: '30px clamp(22px, 5vw, 52px)',
            background: 'linear-gradient(135deg, #6D28D9 0%, #A21CAF 100%)',
            color: '#FFFFFF',
          }}
        >
          <Label color="violet" style={{ marginBottom: 14 }}>Primeros pasos</Label>
          <h1 style={{ fontSize: 'clamp(28px, 5vw, 42px)', lineHeight: 1.12, margin: '0 0 12px' }}>
            Primero sincronizá tus cuentas
          </h1>
          <p style={{ fontSize: 17, lineHeight: 1.6, maxWidth: 700, margin: 0, opacity: 0.95 }}>
            Conectá tu primer canal para que Stocky pueda importar sus publicaciones y centralizar el stock.
          </p>
        </div>

        <div style={pageBodyStyle}>
          <Grid stackable columns={2} verticalAlign="middle">
            <Grid.Column width={9}>
              <div style={{ ...surfaceCardStyle, padding: 'clamp(20px, 4vw, 30px)' }}>
                <div style={{ fontSize: 20, fontWeight: 800, marginBottom: 18 }}>Tu recorrido inicial</div>
                <div style={{ display: 'grid', gap: 18 }}>
                  {[
                    {
                      number: 1,
                      title: 'Conectá una cuenta',
                      description: 'Autorizá Mercado Libre o Tienda Nube desde su acceso oficial.',
                      active: true,
                    },
                    {
                      number: 2,
                      title: 'Importá las publicaciones',
                      description: 'Revisá qué se va a traer y seguí el progreso de la primera sincronización.',
                    },
                    {
                      number: 3,
                      title: 'Empezá a operar',
                      description: 'Stocky agrupa tus publicaciones por SKU y deja listo el control de stock.',
                    },
                  ].map((step) => (
                    <div key={step.number} style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
                      <div
                        style={{
                          width: 36,
                          height: 36,
                          borderRadius: 12,
                          display: 'grid',
                          placeItems: 'center',
                          flexShrink: 0,
                          fontWeight: 800,
                          color: step.active ? '#FFFFFF' : '#7C3AED',
                          background: step.active ? '#7C3AED' : '#F3E8FF',
                        }}
                        aria-hidden="true"
                      >
                        {step.number}
                      </div>
                      <div>
                        <div style={{ fontWeight: 800, fontSize: 17, marginBottom: 4 }}>{step.title}</div>
                        <div style={{ color: '#6B7280', lineHeight: 1.55 }}>{step.description}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </Grid.Column>
            <Grid.Column width={7}>
              <AccountImportCard
                from="/inicio"
                prominent
                description="Elegí tu marketplace, autorizá la cuenta y te acompañamos durante la importación inicial."
              />
            </Grid.Column>
          </Grid>
        </div>
      </Segment>
    </Container>
  );
}

function ListingsNextStep({ accounts }) {
  return (
    <Segment style={{ ...softInfoCardStyle, padding: 22, marginBottom: 20, borderColor: '#C4B5FD' }}>
      <Grid stackable columns={2} verticalAlign="middle">
        <Grid.Column width={10}>
          <Label color="green" basic style={{ marginBottom: 12 }}>
            <Icon name="check circle" /> Cuenta conectada
          </Label>
          <div style={{ fontSize: 24, fontWeight: 800, marginBottom: 8 }}>Ahora importá tus publicaciones</div>
          <div style={{ color: '#6B7280', lineHeight: 1.6, marginBottom: 14 }}>
            Ya completaste la conexión. El próximo paso es traer las publicaciones para organizar el stock por SKU.
          </div>
          <div aria-label="Cuentas conectadas" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {accounts.map((account) => (
              <Label key={`${account.accountType || 'channel'}-${account.id}`} basic>
                <Icon name="plug" /> {getAccountName(account)}
              </Label>
            ))}
          </div>
        </Grid.Column>
        <Grid.Column width={6}>
          <AccountImportCard
            from="/inicio"
            title="Importar publicaciones"
            cta="Continuar con la importación"
            description="Elegí la cuenta, confirmá cómo tratar el stock y seguí el resultado."
          />
        </Grid.Column>
      </Grid>
    </Segment>
  );
}

function OperationsHomePage() {
  const { summary } = useStockInconsistencySummary();
  const { user, getUser } = useAuth();
  const currentUser = user || getUser();
  const authUser = useMemo(
    () => (currentUser?.accessToken ? { accessToken: currentUser.accessToken } : null),
    [currentUser?.accessToken],
  );
  const onboarding = useFirstRunOnboarding(authUser);
  const totalOpen = Number(summary?.totalOpen || 0);
  const critical = Number(summary?.critical || 0);

  if (onboarding.isLoading) {
    return <OnboardingLoadingState />;
  }

  if (onboarding.hasAccounts === false) {
    return <FirstAccountOnboarding />;
  }

  return (
    <Container fluid style={pageShellStyle}>
      <Segment style={pageFrameStyle}>
        <div style={pageBodyStyle}>
          {onboarding.error ? (
            <Message warning>
              <Message.Header>No pudimos completar la verificación inicial</Message.Header>
              <p>{onboarding.error} Podés seguir usando Stocky o volver a intentar.</p>
              <Button size="small" basic onClick={onboarding.retry}>Reintentar</Button>
            </Message>
          ) : null}

          {onboarding.hasAccounts && onboarding.hasListings === false ? (
            <ListingsNextStep accounts={onboarding.accounts} />
          ) : null}

          <Segment style={{ ...softInfoCardStyle, padding: 22, marginBottom: 20 }}>
            <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 8 }}>Resumen de hoy</div>
            <div style={{ color: '#6B7280', lineHeight: 1.6, marginBottom: 14 }}>
              {totalOpen > 0
                ? `Tenés ${totalOpen} problemas abiertos y ${critical} críticos para revisar.`
                : 'No hay diferencias abiertas en este momento.'}
            </div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              <Button as={Link} to="/stock-inconsistencies" color="violet">
                {totalOpen > 0 ? 'Ir a diferencias' : 'Ver diferencias'}
              </Button>
              <Button as={Link} to="/stock" basic>
                Abrir stock
              </Button>
            </div>
          </Segment>

          <div style={sectionTitleStyle}>Áreas clave</div>
          <Grid stackable columns={3}>
            {quickSections.map((section) => (
              <Grid.Column key={section.key}>
                <Segment style={{ ...surfaceCardStyle, padding: 20, height: '100%' }}>
                  <div
                    style={{
                      width: 42,
                      height: 42,
                      borderRadius: 14,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: '#F5EDFF',
                      color: '#7C3AED',
                      marginBottom: 14,
                    }}
                  >
                    <Icon name={section.icon} style={{ margin: 0 }} />
                  </div>
                  <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 8 }}>{section.title}</div>
                  <div style={{ color: '#6B7280', lineHeight: 1.6, minHeight: 52, marginBottom: 16 }}>
                    {section.description}
                  </div>
                  <Button as={Link} to={section.to} basic color="violet">
                    {section.cta}
                  </Button>
                </Segment>
              </Grid.Column>
            ))}
          </Grid>
        </div>
      </Segment>
    </Container>
  );
}

export default OperationsHomePage;
